
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DataTable } from "@/components/ui/data-table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { getProductName } from "@/components/history/ProductUtils";
import { MediaPreviewDialog } from "@/components/history/MediaPreviewDialog";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { useState } from "react";

export const IncidentsTab = () => {
  const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { data: incidents = [] } = useQuery({
    queryKey: ["staff-incidents"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("stock_incidents")
        .select(`
          *,
          stock_items (
            id,
            data,
            quantity
          )
        `)
        .eq("owner_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const handleViewMedia = async (mediaUrls: string[]) => {
    if (!mediaUrls?.length) return;
    
    // Obtenir l'URL publique du premier fichier
    const { data } = await supabase.storage
      .from('incident_proofs')
      .createSignedUrl(mediaUrls[0], 3600); // URL valide pendant 1 heure

    if (data?.signedUrl) {
      setSelectedMediaUrl(data.signedUrl);
      setIsPreviewOpen(true);
    }
  };

  const columns = [
    {
      id: "date",
      label: "Date",
      cell: (row: any) => format(new Date(row.created_at), "Pp", { locale: fr }),
    },
    {
      id: "product",
      label: "Produit",
      cell: (row: any) => getProductName(row.stock_items),
    },
    {
      id: "quantity",
      label: "Quantité",
      cell: (row: any) => row.quantity,
    },
    {
      id: "reason",
      label: "Motif",
      cell: (row: any) => {
        switch (row.reason) {
          case "damaged":
            return "Détruit/Endommagé";
          case "repair":
            return "En réparation";
          case "other":
            return "Autre";
          default:
            return row.reason;
        }
      },
    },
    {
      id: "explanation",
      label: "Explication",
      cell: (row: any) => row.explanation || "-",
    },
    {
      id: "proofs",
      label: "Preuves",
      cell: (row: any) => {
        if (!row.media_urls?.length) return "-";
        return (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => handleViewMedia(row.media_urls)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            {row.media_urls.length} fichier(s)
          </Button>
        );
      },
    },
  ];

  return (
    <Card className="p-6">
      <DataTable
        columns={columns}
        data={incidents}
      />
      
      {selectedMediaUrl && (
        <MediaPreviewDialog
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          url={selectedMediaUrl}
        />
      )}
    </Card>
  );
};
